import * as React from 'react';

import { toast } from 'react-toastify';

import { Error } from 'components/error/Error';
import Form from 'components/form/Form';
import GridNew from 'components/grid/GridNew';
import { Loader } from 'components/loader/Loader';
import { FormLoader } from 'components/form/FormLoader';
import { STATUSES } from 'core/api';
import { MODALS } from 'core/modals/config';
import { IWithModalProps } from 'core/modals/interfaces';
import { withModalsFC } from 'core/modals/ModalsProvider';
import { useAppSelector, useAppDispatch } from 'core/store/createStore';

import { decorators, formConfig, gridConfig } from './config';
import { IProps } from './interfaces';
import {
  checkHashtagInfo,
  createHashtag,
  deleteHashtag,
  editHashtag,
  fetchHashtagInfo,
  fetchHashtags,
  resetHashtagInfo,
} from './redux';

import styles from './Hashtags.module.styl';

const initialValues = {
  name: '',
  slug: '',
  isSlugNotSynchronized: false,
  description: null,
};

const Hashtags: React.FC<IProps & IWithModalProps> = ({
  history,
  location,
  match,
  openModal,
}) => {
  const { hash, search } = location;
  const {
    params: { id },
  } = match;
  const [formError, setFormError] = React.useState<string | null>(null);
  const { list: hashtags, item: hashtag } = useAppSelector(
    state => state.hashtags,
  );
  const dispatch = useAppDispatch();
  const renderForm = () => {
    const {
      status,
      error,
      data: { data: hashtagInfo },
    } = hashtag;

    if (status === STATUSES.FAILED) {
      return <Error>{error}</Error>;
    }

    if ((status === STATUSES.INITIAL && !id) || status === STATUSES.SUCCESS) {
      return (
        <Form
          key={hashtagInfo ? 'update' : 'create'}
          config={formConfig}
          onSubmit={onSubmit}
          validate={validate}
          initialValues={hashtagInfo || initialValues}
          errorMessage={formError}
          decorators={decorators(hashtagInfo || initialValues)}
        />
      );
    }
    return <FormLoader config={formConfig} />;
  };
  const renderGrid = () => {
    const { hash } = location;
    const searchPhrase = hash ? hash.split('#')[1] : '';

    switch (hashtags.status) {
      case STATUSES.REQUEST:
        return <Loader />;
      case STATUSES.FAILED:
        return <Error>{hashtags.error}</Error>;
      case STATUSES.SUCCESS:
        return (
          <GridNew
            data={hashtags.data.items}
            config={gridConfig}
            pagination={hashtags.data.pagination}
            urlPrefix="/admin/hashtags"
            removeItem={removeHashtagHandler}
            searchPhrase={searchPhrase}
            onSearch={handleSearch}
          />
        );
    }
  };
  const handleSearch = (searchPhrase: string) => {
    history.push(`/admin/hashtags#${searchPhrase}`);
  };
  const onSubmit = async (values: any, onSuccess: any) => {
    const preparedValues = {
      description: values.description,
      name: values.name,
      slug: values.slug,
    };

    try {
      if (!id) {
        await dispatch(createHashtag(preparedValues));
      } else {
        await dispatch(editHashtag(id, preparedValues));
      }
    } catch (e) {
      const { message } = e as Error;
      toast.error(message);

      setFormError(message);
      return;
    }
    dispatch(resetHashtagInfo());
    dispatch(fetchHashtags({ hash, search }));
    toast.success('Success!');
    onSuccess();

    history.push('/admin/hashtags');
    return true;
  };
  const removeHashtagHandler = async (hashtagId: string) => {
    let count;

    try {
      const hashtagResponse = await dispatch(checkHashtagInfo(hashtagId));
      //@ts-ignore
      count = hashtagResponse.data.count;
    } catch (e) {
      const { message } = e as Error;

      toast.error(message);
      return;
    }
    if (count > 0) {
      openModal(MODALS.CONFIRM, {
        caption: 'Warning',
        onConfirm: async () => {
          await dispatch(deleteHashtag(hashtagId));
          dispatch(fetchHashtags({ hash, search }));
          toast.success('Success!');
        },
        text: `This hashtags is used in ${count} ${
          count > 1 ? 'articles' : 'article'
        }`,
      });
      return;
    }

    await dispatch(deleteHashtag(hashtagId));
    toast.success('Success!');
    if (id === hashtagId) {
      dispatch(resetHashtagInfo());
      history.push('/admin/hashtags');
    }

    dispatch(fetchHashtags({ hash, search }));
  };
  const validate = async (values: any) => {
    if (formError) {
      setFormError(null);
    }

    const errors: Record<string, string> = {};

    const requiredFields = formConfig.fields
      .flat()
      .filter((field: any) => field.isRequired);

    requiredFields.forEach((field: any) => {
      if (!values[field.name]) {
        errors[field.name] = 'Required Field';
      }
    });

    return errors;
  };

  React.useEffect(() => {
    dispatch(
      fetchHashtags({
        hash,
        search,
      }),
    );
  }, [hash, search, dispatch]);
  React.useEffect(() => {
    if (!id) {
      dispatch(resetHashtagInfo());
    } else {
      dispatch(fetchHashtagInfo(id));
    }

    return () => {
      dispatch(resetHashtagInfo());
    };
  }, [id, dispatch]);

  return (
    <div className={styles.root}>
      {renderForm()}
      {renderGrid()}
    </div>
  );
};

export default withModalsFC(Hashtags);
